import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import {
  fetchListViews,
  fetchListView,
  createListView as createListViewGobodo,
  updateListView as updateListViewGobodo,
  deleteListView as deleteListViewGobodo,
} from '../gobodo/gobodoListViewService';
import { mockObjects } from './objectQueries';

// ============================================
// TYPES
// ============================================

export type FilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterOrEqual'
  | 'lessOrEqual'
  | 'isNull'
  | 'isNotNull';

export interface FilterCondition {
  id: string;
  fieldId: string;
  fieldLabel: string;
  fieldType: string;
  operator: FilterOperator;
  value: string | number | boolean;
}

export interface SortConfig {
  fieldId: string;
  fieldLabel: string;
  direction: 'asc' | 'desc';
}

export interface ListViewColumn {
  fieldId: string;
  fieldLabel: string;
  fieldType: string;
  width?: number; // pixels or percentage
  sortable: boolean;
}

export interface ListView {
  id: string;
  name: string;
  objectName: string;
  description?: string;
  columns: ListViewColumn[];
  filters: FilterCondition[];
  sort: SortConfig[];
  isDefault: boolean;
  isShared: boolean;
  createdBy: string;
  createdDate: string;
  lastModified: string;
}

// ============================================
// MOCK DATA
// ============================================

// Initialize empty array - will be populated dynamically
export const mockListViews: ListView[] = [];

// Generate default list views for all active objects
// Note: Columns will be empty here; they should be populated via the ListViewBuilder UI
// or from the actual fields once the object detail page loads
mockObjects.filter(obj => obj.isActive).forEach(obj => {
  // Add default "All [Object]s" view
  mockListViews.push({
    id: `view_${obj.apiName}_001`,
    name: `All ${obj.pluralLabel}`,
    objectName: obj.apiName,
    description: `Complete list of all ${obj.label.toLowerCase()} records`,
    columns: [], // Empty - will be configured in ListViewBuilder
    filters: [],
    sort: [],
    isDefault: true,
    isShared: true,
    createdBy: 'System',
    createdDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
  });

  // Add "Recent [Object]s" view for core and custom objects
  if (obj.category === 'core' || obj.category === 'custom') {
    mockListViews.push({
      id: `view_${obj.apiName}_002`,
      name: `Recent ${obj.pluralLabel}`,
      objectName: obj.apiName,
      description: `Recently modified ${obj.label.toLowerCase()} records`,
      columns: [], // Empty - will be configured in ListViewBuilder
      filters: [],
      sort: [],
      isDefault: false,
      isShared: true,
      createdBy: 'System',
      createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()
    });
  }
});

// ============================================
// QUERY KEYS
// ============================================

export const listViewKeys = {
  all: ['list-views'] as const,
  lists: () => [...listViewKeys.all, 'list'] as const,
  list: (objectName: string) => [...listViewKeys.lists(), objectName] as const,
  details: () => [...listViewKeys.all, 'detail'] as const,
  detail: (id: string) => [...listViewKeys.details(), id] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all list views for an object
 */
export function useListViews(objectName: string) {
  return useQuery({
    queryKey: listViewKeys.list(objectName),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return fetchListViews(objectName);
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      return mockListViews.filter(view => view.objectName === objectName);
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Get single list view
 */
export function useListView(viewId: string) {
  return useQuery({
    queryKey: listViewKeys.detail(viewId),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return fetchListView(viewId);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const view = mockListViews.find(v => v.id === viewId);
      if (!view) throw new Error('List view not found');
      return view;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!viewId,
  });
}

/**
 * Create new list view
 */
export function useCreateListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (viewData: Omit<ListView, 'id' | 'createdBy' | 'createdDate' | 'lastModified'>) => {
      if (USE_GOBODO_API) {
        return createListViewGobodo(viewData);
      }
      await new Promise(resolve => setTimeout(resolve, 600));
      const newView: ListView = {
        ...viewData,
        id: `view_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      };
      mockListViews.push(newView);
      return newView;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listViewKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Update list view
 */
export function useUpdateListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ viewId, updates }: { viewId: string; updates: Partial<ListView> }) => {
      if (USE_GOBODO_API) {
        return updateListViewGobodo(viewId, updates);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const index = mockListViews.findIndex(v => v.id === viewId);
      if (index === -1) throw new Error('List view not found');
      mockListViews[index] = {
        ...mockListViews[index],
        ...updates,
        lastModified: new Date().toISOString(),
      };
      return mockListViews[index];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listViewKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: listViewKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Delete list view
 */
export function useDeleteListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ viewId, objectName }: { viewId: string; objectName: string }) => {
      if (USE_GOBODO_API) {
        const view = await fetchListView(viewId);
        if (view.isDefault) throw new Error('Cannot delete the default list view');
        await deleteListViewGobodo(viewId);
        return { viewId, objectName };
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      const view = mockListViews.find(v => v.id === viewId);
      if (!view) throw new Error('List view not found');
      if (view.isDefault) throw new Error('Cannot delete the default list view');
      const index = mockListViews.findIndex(v => v.id === viewId);
      mockListViews.splice(index, 1);
      return { viewId, objectName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listViewKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Clone list view
 */
export function useCloneListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (viewId: string) => {
      if (USE_GOBODO_API) {
        const view = await fetchListView(viewId);
        const { id: _id, createdBy: _cb, createdDate: _cd, lastModified: _lm, ...rest } = view;
        return createListViewGobodo({ ...rest, name: `${view.name} (Copy)`, isDefault: false, isShared: false });
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const view = mockListViews.find(v => v.id === viewId);
      if (!view) throw new Error('List view not found');
      const clonedView: ListView = {
        ...view,
        id: `view_${Date.now()}`,
        name: `${view.name} (Copy)`,
        isDefault: false,
        isShared: false,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      };
      mockListViews.push(clonedView);
      return clonedView;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listViewKeys.list(data.objectName) });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get operator label
 */
export function getOperatorLabel(operator: FilterOperator): string {
  const labels: Record<FilterOperator, string> = {
    equals: 'Equals',
    notEquals: 'Does not equal',
    contains: 'Contains',
    notContains: 'Does not contain',
    startsWith: 'Starts with',
    endsWith: 'Ends with',
    greaterThan: 'Greater than',
    lessThan: 'Less than',
    greaterOrEqual: 'Greater or equal',
    lessOrEqual: 'Less or equal',
    isNull: 'Is empty',
    isNotNull: 'Is not empty'
  };
  return labels[operator];
}

/**
 * Get available operators for field type
 */
export function getOperatorsForFieldType(fieldType: string): FilterOperator[] {
  const operatorMap: Record<string, FilterOperator[]> = {
    text: ['equals', 'notEquals', 'contains', 'notContains', 'startsWith', 'endsWith', 'isNull', 'isNotNull'],
    email: ['equals', 'notEquals', 'contains', 'notContains', 'isNull', 'isNotNull'],
    phone: ['equals', 'notEquals', 'contains', 'notContains', 'isNull', 'isNotNull'],
    number: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isNull', 'isNotNull'],
    currency: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isNull', 'isNotNull'],
    date: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isNull', 'isNotNull'],
    datetime: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isNull', 'isNotNull'],
    checkbox: ['equals', 'notEquals'],
    picklist: ['equals', 'notEquals', 'isNull', 'isNotNull'],
    lookup: ['equals', 'notEquals', 'isNull', 'isNotNull']
  };

  return operatorMap[fieldType] || ['equals', 'notEquals', 'isNull', 'isNotNull'];
}

/**
 * Format filter value for display
 */
export function formatFilterValue(value: string | number | boolean, fieldType: string): string {
  if (value === 'CurrentUser') return '(Current User)';
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (fieldType === 'currency') return `$${Number(value).toLocaleString()}`;
  return String(value);
}
